import { radiansToCardinalDirection } from "game-common/util"
import { Entity } from "game-common/models"
import { AttackWindupView } from "game-common/charged_weapon"
import { Graphics } from "pixi.js"
import { ScalingParams } from "../../../../client_util"
import { BombTrajectoryTracer } from "./bomb_trajectory_tracer"
import { drawBomb, drawMaterializingBomb } from "./bomb_art"

/** Locked landing geometry and preparation cue, rendered in world space above scenery. */
export class AttackWindupWarning extends Graphics {
    readonly heldBomb = new Graphics()
    readonly tracer = new BombTrajectoryTracer()
    private view?: AttackWindupView
    private receivedAt = 0
    private origin = { x: 0, y: 0 }
    update = (entity: Entity, now = Date.now()) => {
        if (entity.attackWindup !== this.view) this.receivedAt = now
        this.view = entity.hp > 0 && !entity.isDead() ? entity.attackWindup : undefined
        this.origin = entity.getBounds().center()
        this.animate(now)
    }
    animate = (now = Date.now(), visible = true,
        grip?: { x: number; y: number; scale: number; hands?: { x: number; y: number }[] }) => {
        this.clear()
        this.heldBomb.clear()
        this.heldBomb.visible = false
        this.visible = visible
        const view = this.view
        if (!view || view.phase === "recovery" || view.remainingMs <= now - this.receivedAt) {
            this.tracer.fade(now)
            this.tracer.animate(now, visible)
            return
        }
        this.tracer.show(this.origin, view.target)
        this.tracer.animate(now, visible)
        if (!visible) return
        const scale = ScalingParams.surfaceZoom
        this.position.set(view.target.x * scale, view.target.y * scale)
        this.scale.set(scale)
        this.lineStyle(3, 0x18100a, 0.9)
        this.beginFill(0xe36c24, 0.12)
        this.drawCircle(0, 0, view.radius)
        this.endFill()
        this.lineStyle(1.5, 0xffba55, 0.95)
        this.drawCircle(0, 0, view.radius)
        this.moveTo(-6, 0); this.lineTo(6, 0)
        const t = Math.max(0, Math.min(1, 1 - (view.remainingMs - now + this.receivedAt) / view.durationMs))
        this.lineStyle(3, 0xffe8aa, 0.9)
        this.moveTo(0, -view.radius)
        this.arc(0, 0, view.radius, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * t)
        const remaining = Math.max(0, view.remainingMs - now + this.receivedAt)
        const holding = view.phase === "windup" || view.phase === "throw" && remaining > view.durationMs / 2
        if (holding && grip && this.parent) {
            const north = radiansToCardinalDirection(view.angle) === "Up"
            this.heldBomb.visible = view.phase === "windup" || !north
            this.heldBomb.position.copyFrom(this.parent.toLocal(grip))
            // The held bomb is smaller than the committed projectile so it fits between the palms.
            this.heldBomb.scale.set(grip.scale * 0.6)
            if (!north) {
                if (view.phase === "windup") drawMaterializingBomb(this.heldBomb, 0, 0, now, t)
                else drawBomb(this.heldBomb, 0, 0, now)
            }
            if (view.phase === "windup") grip.hands?.forEach((hand, index) => {
                const start = this.heldBomb.toLocal(hand)
                drawCastingArc(this.heldBomb, start.x, start.y, now, index, north)
            })
        }
    }
}

/** Two strokes share each jittered path: a blue glow and a bright electric core. */
export const drawCastingArc = (art: Graphics, x: number, y: number, now: number, seed: number, aroundHead = false) => {
    const distance = Math.hypot(x, y)
    if (distance < 1) return
    const endX = aroundHead ? Math.sign(x) * 6 : x / distance * 6
    const endY = aroundHead ? 0 : y / distance * 6
    const phase = Math.floor(now / 55)
    // Keep the northern arcs outside the skull until below it, then converge behind the body.
    const route: [number, number][] = aroundHead ?
        [[x, y], [x, Math.max(y, 6)], [endX, Math.max(y, 6)], [endX, endY]] :
        [[x, y], [endX, endY]]
    const points: [number, number][] = [route[0]]
    for (let segment = 1; segment < route.length; segment++) {
        const from = route[segment - 1], to = route[segment]
        const dx = to[0] - from[0], dy = to[1] - from[1]
        const length = Math.hypot(dx, dy) || 1
        for (let i = 1; i < 5; i++) {
            const t = i / 5
            const jitter = Math.sin(phase * 2.7 + seed * 4.1 + i * 7.3 + segment) * 2.5
            points.push([from[0] + dx * t - dy / length * jitter,
                from[1] + dy * t + dx / length * jitter])
        }
        points.push(to)
    }
    for (const [width, color, alpha] of [[3, 0x358dff, 0.35], [1, 0xd7f7ff, 0.95]]) {
        art.lineStyle(width, color, alpha)
        art.moveTo(points[0][0], points[0][1])
        for (const point of points.slice(1)) art.lineTo(point[0], point[1])
    }
}
